import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useAuth } from '../auth/AuthContext';
import { usePreferences } from '../hooks/usePreferences';
import { clientLogger } from '@/lib/clientLogger';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type Theme = 'dark' | 'light';

export interface ThemeContextValue {
  /** Current theme ('dark' or 'light') */
  theme: Theme;
  /** Set theme explicitly */
  setTheme: (theme: Theme) => void;
  /** Toggle between dark and light */
  toggleTheme: () => void;
  /** True while loading theme preference */
  isLoading: boolean;
}

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const THEME_STORAGE_KEY = 'st-theme';
const DEFAULT_THEME: Theme = 'dark';

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/**
 * Get system color scheme preference
 * NOTE: Light mode is currently retired. Always returns 'dark' to prevent white flash.
 * When light mode is re-enabled, restore the original logic:
 *   return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
 */
const getSystemTheme = (): Theme => {
  // Light mode retired - always return dark to prevent white flash
  return DEFAULT_THEME;
};

/**
 * Get theme from localStorage (for unauthenticated users)
 */
const getStoredTheme = (): Theme | null => {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  return null;
};

/**
 * Store theme in localStorage (for unauthenticated users)
 */
const storeTheme = (theme: Theme): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(THEME_STORAGE_KEY, theme);
};

/**
 * Apply theme to the document
 */
const applyTheme = (theme: Theme): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
};

/**
 * Get initial theme synchronously to prevent flash
 * Priority: localStorage > system preference > default
 */
const getInitialTheme = (): Theme => {
  const stored = getStoredTheme();
  if (stored) return stored;
  return getSystemTheme();
};

// -----------------------------------------------------------------------------
// Script to inject in head for flash prevention (SSR-compatible)
// -----------------------------------------------------------------------------

/**
 * Inline script that runs before React hydration to set initial theme.
 * This prevents flash of wrong theme on page load.
 * NOTE: Light mode retired - always defaults to 'dark'
 */
export const ThemeScript: React.FC = () => {
  const script = `
    (function() {
      var stored = localStorage.getItem('${THEME_STORAGE_KEY}');
      // Light mode retired - always default to dark to prevent white flash
      var theme = (stored === 'dark' || stored === 'light') ? stored : 'dark';
      // Force dark if light mode somehow got stored (safety measure)
      if (theme === 'light') theme = 'dark';
      document.documentElement.setAttribute('data-theme', theme);
    })();
  `;
  return <script dangerouslySetInnerHTML={{ __html: script }} />;
};

// -----------------------------------------------------------------------------
// Context
// -----------------------------------------------------------------------------

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// -----------------------------------------------------------------------------
// Provider
// -----------------------------------------------------------------------------

interface ThemeProviderProps {
  children: React.ReactNode;
  /** Optional default theme override (useful for testing) */
  defaultTheme?: Theme;
}

/**
 * ThemeProvider - Manages theme state and persistence
 *
 * For authenticated users: syncs with Supabase via usePreferences
 * For unauthenticated users: uses localStorage
 *
 * Usage:
 * ```tsx
 * <AuthProvider>
 *   <ThemeProvider>
 *     <App />
 *   </ThemeProvider>
 * </AuthProvider>
 * ```
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme,
}) => {
  const { user, isAuthReady } = useAuth();
  const { preferences, isLoading: prefsLoading, updateTheme } = usePreferences();

  // Initialize theme synchronously to prevent flash
  const [theme, setThemeState] = useState<Theme>(() => defaultTheme ?? getInitialTheme());
  const [isInitialized, setIsInitialized] = useState(false);
  // Track whether we've synced from preferences (only do this once)
  const [hasSyncedFromPrefs, setHasSyncedFromPrefs] = useState(false);

  // Apply theme to document immediately on state change
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Apply initial theme on mount (before React hydration completes)
  useEffect(() => {
    const initial = defaultTheme ?? getInitialTheme();
    applyTheme(initial);
  }, [defaultTheme]);

  // Sync with user preferences when authenticated and preferences are loaded
  // This only runs ONCE when preferences first load - after that, local state is source of truth
  useEffect(() => {
    if (!isAuthReady) return;

    if (user && !prefsLoading) {
      // Only sync from preferences once on initial load
      if (!hasSyncedFromPrefs) {
        const userTheme = preferences.theme;
        if (userTheme && userTheme !== theme) {
          setThemeState(userTheme);
        }
        setHasSyncedFromPrefs(true);
      }
      setIsInitialized(true);
    } else if (!user) {
      // No user - we already have localStorage/system theme
      setIsInitialized(true);
    }
  }, [user, isAuthReady, preferences.theme, prefsLoading, hasSyncedFromPrefs]);

  // Listen for system preference changes
  // NOTE: Light mode retired - this listener is disabled to prevent white flash
  // Uncomment when light mode is re-enabled
  /*
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-switch if user hasn't set an explicit preference
      const stored = getStoredTheme();
      if (!stored && !user) {
        const newTheme = e.matches ? 'dark' : 'light';
        setThemeState(newTheme);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [user]);
  */

  const setTheme = useCallback(
    async (newTheme: Theme) => {
      setThemeState(newTheme);
      storeTheme(newTheme);

      // Persist to Supabase if authenticated
      if (user) {
        try {
          await updateTheme(newTheme);
        } catch (error) {
          clientLogger.error('Failed to persist theme preference:', error);
        }
      }
    },
    [user, updateTheme]
  );

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  }, [theme, setTheme]);

  const isLoading = !isInitialized || (user && prefsLoading);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme,
      toggleTheme,
      isLoading,
    }),
    [theme, setTheme, toggleTheme, isLoading]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

/**
 * useTheme hook - Access theme state and controls
 *
 * Usage:
 * ```tsx
 * const { theme, toggleTheme, setTheme } = useTheme();
 *
 * // Toggle between dark and light
 * <button onClick={toggleTheme}>Toggle Theme</button>
 *
 * // Set specific theme
 * <button onClick={() => setTheme('dark')}>Dark Mode</button>
 *
 * // Read current theme
 * <div className={theme === 'dark' ? 'dark-styles' : 'light-styles'} />
 * ```
 */
export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within <ThemeProvider />');
  }
  return ctx;
};
